import { createSelector } from '@reduxjs/toolkit';

import { QUALITY_TABLE_HEADERS } from '../../../../modules/commentary/consts.ts';
import { typeSafeObjectEntries } from '../../../utils/typeSafeObjectEntries.ts';
import {
  selectAppliedQualityAdjsDisplay,
  selectQualityAdjsOutput,
} from '../adjustmentSelectors/selectQualityAdj.ts';
import { selectAdjustmentSettings, selectCompSequence, selectCompTypes } from '../orderSelectors.ts';
import {
  selectImprovementsEntities,
  selectSubjectIsManufacturedHome,
} from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectManualAdjustmentsEntities } from '../propertyDataSelectors/manualAdjustmentsSelectors.ts';
import { selectOrderHasManufacturedAndNonManufacturedProperties } from '../selectOrderHasManufacturedAndNonManufacturedProperties.ts';
import { getCommentaryTableHelperText } from '../utils/getCommentaryTableHelperText.ts';
import { isAivRow, isArvRow } from './utils.ts';

const getPropertyLabel = (
  mixedOrder: boolean,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  id: string,
) => {
  if (!mixedOrder) return '';

  const propertyType = improvements[id]?.propertyType;
  switch (propertyType) {
    case 'manufacturedHome':
      return ' (Manufactured Home)';
    case 'singleFamilyResidence':
      return ' (Single-Family Residence)';
    default:
      return '';
  }
};

const getFoundationLabel = (
  improvementEntities: ReturnType<typeof selectImprovementsEntities>,
  id: string,
) => {
  const improvements = improvementEntities[id];
  const foundation =
    improvements?.propertyType === 'manufacturedHome' ? improvements?.foundation : undefined;

  switch (foundation) {
    case 'slab':
      return 'Slab Foundation';
    case 'pierAndBeam':
    default:
      return 'Pier and Beam Foundation';
  }
};

const selectQualityCommentaryData = createSelector(
  [
    selectQualityAdjsOutput,
    selectAppliedQualityAdjsDisplay,
    selectAdjustmentSettings,
    selectOrderHasManufacturedAndNonManufacturedProperties,
    selectImprovementsEntities,
    selectSubjectIsManufacturedHome,
    selectManualAdjustmentsEntities,
    selectCompSequence,
  ],
  (
    adjustments,
    appliedAdjustments,
    adjustmentSettings,
    mixedOrder,
    improvements,
    subjectIsManufacturedHome,
    manualAdjustmentsEntities,
    compSequence,
  ) => ({
    helperText: getCommentaryTableHelperText(
      adjustmentSettings,
      'quality',
      undefined,
      manualAdjustmentsEntities,
    ),
    data: typeSafeObjectEntries(adjustments).sort((a, b) => {
      if (a[0] === 'subjectArv' || a[0] === 'subject') return -1;
      if (b[0] === 'subjectArv' || b[0] === 'subject') return 1;
      return 0;
    }).map(([id, adj]) => {
      const qualityLevel = [
        `${adj?.compQuality.toString()}${getPropertyLabel(mixedOrder, improvements, id)}`,
      ];
      if (subjectIsManufacturedHome) {
        qualityLevel.push(getFoundationLabel(improvements, id));
      }
      return {
        label: (id === 'subject' || id === 'subjectArv') ? 'SUB' : `CP ${compSequence.indexOf(id) + 1}`,
        qualityLevel,
        costNewAtSubjectQuality: adj?.costNewAtSubjectQuality.toString(true),
        costNew: adj?.costNewAtCompQuality.toString(true),
        costNewDifference:
          adj?.differenceInCost && adj.differenceInCost.unitAmount !== 0
            ? adj?.differenceInCost.toString(true)
            : undefined,
        remainingEconomicLife: adj?.remainingEcoLife
          ? `${(adj.remainingEcoLife * 100).toFixed(1)}%`
          : undefined,
        adjustment:
          adj?.adjustment && adj?.adjustment.unitAmount !== 0
            ? adj?.adjustment.toString(true)
            : undefined,
        appliedAdjustments: appliedAdjustments[id]?.value
          ? appliedAdjustments[id]?.value
          : undefined,
        id,
      };
    }),
  }),
);

export const selectQualityCommentaryTableDisplay = createSelector(
  [selectQualityCommentaryData, selectCompTypes],
  ({
    helperText,
    data,
  }, compTypes): { helperText?: string; data: [string[], ...Array<string | string[]>[]] } => ({
    helperText,
    data: [
      QUALITY_TABLE_HEADERS,
      ...data.filter((row) => isAivRow(row, compTypes)).map((row) => [
        row.label ?? '-',
        row.qualityLevel ?? '-',
        row.costNewAtSubjectQuality ?? '-',
        row.costNew ?? '-',
        row.costNewDifference ?? '-',
        row.remainingEconomicLife ?? '-',
        row.adjustment ?? '-',
        row.appliedAdjustments ?? '-',
      ]),
    ],
  }),
);

export const selectArvQualityCommentaryTableDisplay = createSelector(
  [selectQualityCommentaryData, selectCompTypes],
  ({
    helperText,
    data,
  }, compTypes): { helperText?: string; data: [string[], ...Array<string | string[]>[]] } => ({
    helperText,
    data: [
      QUALITY_TABLE_HEADERS,
      ...data.filter((row) => isArvRow(row, compTypes)).map((row) => [
        row.label ?? '-',
        row.qualityLevel ?? '-',
        row.costNewAtSubjectQuality ?? '-',
        row.costNew ?? '-',
        row.costNewDifference ?? '-',
        row.remainingEconomicLife ?? '-',
        row.adjustment ?? '-',
        row.appliedAdjustments ?? '-',
      ]),
    ],
  }),
);
